import type { PaymentPlatform } from '@forest/paywall-core'
import {
  classifyShopPurchaseFailure,
  purchaseShopProduct,
} from '~/services/shop-purchase'
import {
  type IapProduct,
  IapPurchaseError,
  purchaseIapProduct,
} from '~/services/iap-purchase'
import type { PurchaseAdapter, PurchaseOutcome } from './machine'

/**
 * The atomic soft-currency Shop product purchase (GLOSSARY.md: Shop product
 * purchase). Checks affordability before submitting, so a local shortage
 * resolves to `insufficient` without a request.
 */
export function softPurchaseAdapter(input: {
  affordable: () => boolean
  productId: () => number
  userId: () => number
}): PurchaseAdapter {
  return {
    connectionDismissal: 'close-shop',
    execute: async (): Promise<PurchaseOutcome> => {
      if (!input.affordable()) return { kind: 'insufficient' }
      const productId = input.productId()
      try {
        await purchaseShopProduct(input.userId(), productId)
      } catch (error) {
        return classifyShopPurchaseFailure(error)
      }
      return { kind: 'committed', productId }
    },
    pendingPresentation: 'locked-confirmation',
  }
}

/**
 * A free product's unlock (GLOSSARY.md: Free product): the same purchase
 * command with nothing to spend, so no affordability check. Its pending
 * state presents inline in the detail it started from, and a 422 is an
 * ordinary rejection — with no balance involved there is nothing to top up.
 */
export function freeUnlockAdapter(input: {
  productId: () => number
  userId: () => number
}): PurchaseAdapter {
  const soft = softPurchaseAdapter({ ...input, affordable: () => true })
  return {
    ...soft,
    execute: async () => {
      const outcome = await soft.execute()
      return outcome.kind === 'insufficient'
        ? { code: 'shop_product_purchase: 422', kind: 'rejected' }
        : outcome
    },
    pendingPresentation: 'inline',
  }
}

/**
 * Wraps an adapter to capture an amount at the moment the purchase is
 * submitted, whatever it reads by the time the purchase lands: the effective
 * price the ledger debits (GLOSSARY.md: Effective price, ADR 0027), or the
 * First-purchase bonus a Time Crystal purchase carries.
 */
export function captureSubmittedAmount(
  adapter: PurchaseAdapter,
  amount: () => number,
): { adapter: PurchaseAdapter; submittedAmount: () => number } {
  let captured = 0
  return {
    adapter: {
      ...adapter,
      execute: () => {
        captured = amount()
        return adapter.execute()
      },
    },
    submittedAmount: () => captured,
  }
}

/**
 * A real-money IAP of any product with a platform SKU: a Time Crystal pack
 * (GLOSSARY.md: Shop Time Crystal purchase; ADR 0016) or the starter pack
 * (ADR 0044). Connection failures happen before native payment begins, so
 * dismissal returns to where the purchase started for a safe retry — the
 * Time Crystal confirmation, or the starter pack's card or detail.
 */
export function iapPurchaseAdapter(input: {
  closeTransactionFailureEvent: string
  deliveryTimeoutEvent: string
  platform: PaymentPlatform
  product: () => IapProduct
  userId: () => number
}): PurchaseAdapter {
  return {
    connectionDismissal: 'return-to-start',
    execute: async (): Promise<PurchaseOutcome> => {
      const product = input.product()
      try {
        await purchaseIapProduct({
          closeTransactionFailureEvent: input.closeTransactionFailureEvent,
          deliveryTimeoutEvent: input.deliveryTimeoutEvent,
          platform: input.platform,
          product,
          userId: input.userId(),
        })
      } catch (error) {
        if (!(error instanceof IapPurchaseError)) {
          console.error('[shop] Unexpected IAP purchase failure', error)
          return {
            code: error instanceof Error ? error.message : String(error),
            kind: 'rejected',
          }
        }
        const failure = error.failure
        switch (failure.kind) {
          case 'cancelled':
            return { kind: 'cancelled' }
          case 'confirmation_pending':
            return { kind: 'confirmation_pending' }
          case 'connection':
            return { kind: 'connection' }
          case 'delivery':
            return { kind: 'delivery', orderId: failure.purchaseId }
          case 'rejected':
            return { code: failure.code, kind: 'rejected' }
        }
      }
      return { kind: 'committed', productId: product.productId }
    },
    pendingPresentation: 'processing-overlay',
  }
}
